import * as i0 from '@angular/core';
import { InjectionToken, isDevMode, Component, ChangeDetectionStrategy, Inject, Optional, Input, NgModule } from '@angular/core';
import { start, end } from 'perf-marks/marks';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
const _c0 = (a0, a1, a2, a3) => ({
  circle: a0,
  progress: a1,
  "progress-dark": a2,
  pulse: a3
});
function NgxSkeletonLoaderComponent_span_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "span", 1);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction4(4, _c0, ctx_r0.appearance === "circle", ctx_r0.animation === "progress", ctx_r0.animation === "progress-dark", ctx_r0.animation === "pulse"))("ngStyle", ctx_r0.theme);
    i0.ɵɵattribute("aria-label", ctx_r0.ariaLabel)("aria-valuetext", ctx_r0.loadingText);
  }
}
const NGX_SKELETON_LOADER_CONFIG = new InjectionToken('ngx-skeleton-loader.config');
let NgxSkeletonLoaderComponent = /*#__PURE__*/(() => {
  class NgxSkeletonLoaderComponent {
    constructor(config) {
      const {
        appearance = 'line',
        animation = 'progress',
        theme = null,
        loadingText = 'Loading...',
        count = 1,
        ariaLabel = 'loading'
      } = config || {};
      this.appearance = appearance;
      this.animation = animation;
      this.theme = theme;
      this.loadingText = loadingText;
      this.count = count;
      this.items = [];
      this.ariaLabel = ariaLabel;
    }
    ngOnInit() {
      start('NgxSkeletonLoader:Rendered');
      start('NgxSkeletonLoader:Loaded');
      this.validateInputValues();
    }
    validateInputValues() {
      // Checking if it's receiving a numeric value (string having ONLY numbers or if it's a number)
      if (!/^\d+$/.test(`${this.count}`)) {
        // Shows error message only in Development
        if (isDevMode()) {
          console.error(`\`NgxSkeletonLoaderComponent\` need to receive 'count' a numeric value. Forcing default to "1".`);
        }
        this.count = 1;
      }
      this.items.length = this.count;
      const allowedAnimations = ['progress', 'progress-dark', 'pulse', 'false'];
      if (allowedAnimations.indexOf(String(this.animation)) === -1) {
        // Shows error message only in Development
        if (isDevMode()) {
          console.error(`\`NgxSkeletonLoaderComponent\` need to receive 'animation' as: ${allowedAnimations.join(', ')}. Forcing default to "progress".`);
        }
        this.animation = 'progress';
      }
      if (['circle', 'line', ''].indexOf(String(this.appearance)) === -1) {
        // Shows error message only in Development
        if (isDevMode()) {
          console.error(`\`NgxSkeletonLoaderComponent\` need to receive 'appearance' as: circle or line or empty string. Forcing default to "''".`);
        }
        this.appearance = '';
      }
    }
    ngOnChanges(changes) {
      // Avoiding multiple calls for the same input in case there's no changes in the fields
      // Checking if the fields that require validation are available and if they were changed
      // In case were not changed, we stop the function. Otherwise, `validateInputValues` will be called.
      if (['count', 'animation', 'appearance'].find(key => changes[key] && (changes[key].isFirstChange() || changes[key].previousValue === changes[key].currentValue))) {
        return;
      }
      this.validateInputValues();
    }
    ngAfterViewInit() {
      end('NgxSkeletonLoader:Rendered');
    }
    ngOnDestroy() {
      end('NgxSkeletonLoader:Loaded');
    }
  }
  /** @nocollapse */ /** @nocollapse */
  /** @nocollapse */ /** @nocollapse */NgxSkeletonLoaderComponent.ɵfac = function NgxSkeletonLoaderComponent_Factory(t) {
    return new (t || NgxSkeletonLoaderComponent)(i0.ɵɵdirectiveInject(NGX_SKELETON_LOADER_CONFIG, 8));
  };
  NgxSkeletonLoaderComponent.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
    type: NgxSkeletonLoaderComponent,
    selectors: [["ngx-skeleton-loader"]],
    inputs: {
      count: "count",
      loadingText: "loadingText",
      appearance: "appearance",
      animation: "animation",
      ariaLabel: "ariaLabel",
      theme: "theme"
    },
    features: [i0.ɵɵNgOnChangesFeature],
    decls: 1,
    vars: 1,
    consts: [["class", "loader", "aria-busy", "true", "aria-valuemin", "0", "aria-valuemax", "100", "role", "progressbar", "tabindex", "0", 3, "ngClass", "ngStyle", 4, "ngFor", "ngForOf"], ["aria-busy", "true", "aria-valuemin", "0", "aria-valuemax", "100", "role", "progressbar", "tabindex", "0", 1, "loader", 3, "ngClass", "ngStyle"]],
    template: function NgxSkeletonLoaderComponent_Template(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵtemplate(0, NgxSkeletonLoaderComponent_span_0_Template, 1, 9, "span", 0);
      }
      if (rf & 2) {
        i0.ɵɵproperty("ngForOf", ctx.items);
      }
    },
    dependencies: [i1.NgForOf, i1.NgClass, i1.NgStyle],
    styles: [".loader[_ngcontent-%COMP%]{box-sizing:border-box;overflow:hidden;position:relative;background:#eff1f6 no-repeat;border-radius:4px;width:100%;height:20px;display:inline-block;margin-bottom:10px;will-change:transform}.loader[_ngcontent-%COMP%]:after, .loader[_ngcontent-%COMP%]:before{box-sizing:border-box}.loader.circle[_ngcontent-%COMP%]{width:40px;height:40px;margin:5px;border-radius:50%}.loader.progress[_ngcontent-%COMP%], .loader.progress-dark[_ngcontent-%COMP%]{transform:translate(0)}.loader.progress[_ngcontent-%COMP%]:after, .loader.progress[_ngcontent-%COMP%]:before, .loader.progress-dark[_ngcontent-%COMP%]:after, .loader.progress-dark[_ngcontent-%COMP%]:before{box-sizing:border-box}.loader.progress[_ngcontent-%COMP%]:before, .loader.progress-dark[_ngcontent-%COMP%]:before{-webkit-animation:_ngcontent-%COMP%_progress 2s ease-in-out infinite;animation:_ngcontent-%COMP%_progress 2s ease-in-out infinite;background-size:200px 100%;position:absolute;z-index:1;top:0;left:0;width:200px;height:100%;content:\"\"}.loader.progress[_ngcontent-%COMP%]:before{background-image:linear-gradient(90deg,rgba(255,255,255,0),rgba(255,255,255,.6),rgba(255,255,255,0))}.loader.progress-dark[_ngcontent-%COMP%]:before{background-image:linear-gradient(90deg,transparent,rgba(0,0,0,.2),transparent)}.loader.pulse[_ngcontent-%COMP%]{-webkit-animation:_ngcontent-%COMP%_pulse 1.5s cubic-bezier(.4,0,.2,1) infinite;animation:_ngcontent-%COMP%_pulse 1.5s cubic-bezier(.4,0,.2,1) infinite;-webkit-animation-delay:.5s;animation-delay:.5s}@media (prefers-reduced-motion: reduce){.loader.pulse[_ngcontent-%COMP%], .loader.progress-dark[_ngcontent-%COMP%], .loader.progress[_ngcontent-%COMP%]{-webkit-animation:none;animation:none}.loader.progress[_ngcontent-%COMP%], .loader.progress-dark[_ngcontent-%COMP%]{background-image:none}}@-webkit-keyframes _ngcontent-%COMP%_progress{0%{transform:translate(-200px)}to{transform:translate(calc(200px + 100vw))}}@keyframes _ngcontent-%COMP%_progress{0%{transform:translate(-200px)}to{transform:translate(calc(200px + 100vw))}}@-webkit-keyframes _ngcontent-%COMP%_pulse{0%{opacity:1}50%{opacity:.4}to{opacity:1}}@keyframes _ngcontent-%COMP%_pulse{0%{opacity:1}50%{opacity:.4}to{opacity:1}}"],
    changeDetection: 0
  });
  return NgxSkeletonLoaderComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let NgxSkeletonLoaderModule = /*#__PURE__*/(() => {
  class NgxSkeletonLoaderModule {
    static forRoot(config) {
      return {
        ngModule: NgxSkeletonLoaderModule,
        providers: [{
          provide: NGX_SKELETON_LOADER_CONFIG,
          useValue: config
        }]
      };
    }
  }
  /** @nocollapse */ /** @nocollapse */
  /** @nocollapse */ /** @nocollapse */
  /** @nocollapse */ /** @nocollapse */NgxSkeletonLoaderModule.ɵfac = function NgxSkeletonLoaderModule_Factory(t) {
    return new (t || NgxSkeletonLoaderModule)();
  };
  NgxSkeletonLoaderModule.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
    type: NgxSkeletonLoaderModule
  });
  NgxSkeletonLoaderModule.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
    imports: [[CommonModule]]
  });
  return NgxSkeletonLoaderModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * Public API Surface of ngx-skeleton-loader
 */

/**
 * Generated bundle index. Do not edit.
 */

export { NGX_SKELETON_LOADER_CONFIG, NgxSkeletonLoaderComponent, NgxSkeletonLoaderModule };
